import { del, get, post, put } from '@/utils';
import { AxiosError } from 'axios';
import i18n from '@/i18n';
// Import DIRECT (nu prin barrel-ul '@/components') — evită ciclul hooks ↔ components.
import {
	IServerColumnConfig,
	IServerTableConfig
} from '@/components/table/column-config';
import { ICreateFSP, IFSP, IUpdateFSP } from './types';

type IFSPResponse = {
	items?: IFSP[];
	pageNumber?: number;
	totalPages?: number;
	totalCount?: number;
	orderBy?: string | null;
	ascending?: boolean;
	hasPreviousPage?: boolean;
	hasNextPage?: boolean;
};

// BE a trecut la LISTĂ simplă (fără envelope) pentru api/administrations — paginarea se
// sintetizează local (1 pagină, count = lungimea listei). Citirea envelope-ului rămâne
// ca fallback, dacă BE revine la forma paginată { items, totalPages, totalCount }.
function parseAdministrations(
	raw: IFSP[] | IFSPResponse | undefined,
	onResponse?: (page: number, items: number) => void
): IFSP[] {
	if (Array.isArray(raw)) {
		onResponse && onResponse(1, raw.length);
		return raw;
	}
	const items = raw?.items || [];
	onResponse && onResponse(raw?.totalPages ?? 1, raw?.totalCount ?? items.length);
	return items;
}

async function fetchFSPs(
	getAll: boolean,
	page: number,
	items: number,
	onResponse?: (page: number, items: number) => void
): Promise<IFSP[]> {
	try {
		const pageVariable = getAll ? 'all' : page + 1;
		const res = await get(`api/administrations?PageNumber=${pageVariable}&PageSize=${items}`);
		return parseAdministrations(res.data as IFSP[] | IFSPResponse, onResponse);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('administrations.errorLoad'));
	}
}

async function fetchQueryFSPs(
	getAll: boolean,
	query: string,
	page: number,
	items: number,
	onResponse?: (page: number, items: number) => void
): Promise<IFSP[]> {
	try {
		const pageVariable = getAll ? 'all' : page + 1;
		const res = await get(
			`api/administrations${query}&PageNumber=${pageVariable}&PageSize=${items}`
		);
		return parseAdministrations(res.data as IFSP[] | IFSPResponse, onResponse);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('administrations.errorLoad'));
	}
}

async function fetchSingleFSP(moderatroId: number): Promise<IFSP> {
	try {
		const res = await get(`api/administrations/${moderatroId}`);
		const data = res.data as IFSP;
		return data || {};
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('administrations.errorLoadOne'));
	}
}

async function deleteFSP(administrationId: number): Promise<string | number> {
	try {
		const res = await del(`api/administrations/${administrationId}`);
		return res?.status === 204 ? 204 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('administrations.errorDelete'));
	}
}

async function postFSP(FSPObj: ICreateFSP): Promise<string | number> {
	try {
		const res = await post('api/administrations', { ...FSPObj });
		return res?.status === 201 ? 201 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('administrations.errorCreate'));
	}
}

async function patchFSP(FSPId: number, updatedFSP: IUpdateFSP): Promise<string | number> {
	try {
		const res = await put(`api/administrations/${FSPId}`, { ...updatedFSP });
		return res?.status === 200 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('administrations.errorUpdate'));
	}
}

// GET api/administrations/table-config — configurația coloanelor tabelului Administrații
// (chei, etichete, ordine, vizibilitate/sortabilitate implicite). Se corelează în pagină
// cu definițiile locale prin applyServerColumnConfig.
async function fetchAdministrationsTableConfig(): Promise<IServerColumnConfig[]> {
	try {
		const res = await get('api/administrations/table-config');
		const data = res.data as IServerTableConfig;
		return data?.columns ?? [];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('administrations.errorTableConfig'));
	}
}

export {
	deleteFSP,
	fetchAdministrationsTableConfig,
	fetchFSPs,
	fetchQueryFSPs,
	fetchSingleFSP,
	patchFSP,
	postFSP
};

